import React, { useState, useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from "react-redux";
import { Card, Table, Typography, Select, InputNumber, Spin, Tag, Progress, Statistic, Row, Col, Tooltip, Avatar, Result, Alert } from 'antd';
import { useKeycloak } from "@react-keycloak/web";
import { getSummaryEmployee } from '../../redux/slieces/summaryEmplyeeSlice';
import styles from './SummaryEmployeeList.module.css';
import WeekDetailModal from './WeekDetailModal';
import { useTranslation } from 'react-i18next';
import {
  UserOutlined,
  CalendarOutlined,
  ClockCircleOutlined,
  FilterOutlined,
  TrophyOutlined,
  BarChartOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  MinusCircleOutlined,
  LoadingOutlined,
  SyncOutlined,
  ScheduleOutlined,
  QuestionCircleOutlined
} from '@ant-design/icons';

const { Title, Text } = Typography;
const { Option } = Select;

// Testdaten mit mehreren Jahren
const testData = [
  {
    userId: 'u1',
    userName: 'Anna Schmidt',
    totalPaidOvertimeHours: 5,
    totalPaidOvertimeMinutes: 30,
    summaryCalendarEmployees: [
      {
        year: 2024,
        weekCalendarNumber: 52,
        actualWorkTimeHours: 38,
        actualWorkTimeMinutes: 0,
        shouldWorkTimeHours: 40,
        shouldWorkTimeMinutes: 0,
        actualHolidayDays: 0,
        shouldHolidayDaysInYear: 25
      },
      {
        year: 2025,
        weekCalendarNumber: 1,
        actualWorkTimeHours: 35,
        actualWorkTimeMinutes: 30,
        shouldWorkTimeHours: 40,
        shouldWorkTimeMinutes: 0,
        actualHolidayDays: 1,
        shouldHolidayDaysInYear: 25
      }
    ]
  },
  {
    userId: 'u2',
    userName: 'Max Müller',
    totalPaidOvertimeHours: 0,
    totalPaidOvertimeMinutes: 0,
    summaryCalendarEmployees: [
      {
        year: 2025,
        weekCalendarNumber: 2,
        actualWorkTimeHours: 40,
        actualWorkTimeMinutes: 0,
        shouldWorkTimeHours: 40,
        shouldWorkTimeMinutes: 0,
        actualHolidayDays: 0,
        shouldHolidayDaysInYear: 30
      },
      {
        year: 2024,
        weekCalendarNumber: 50,
        actualWorkTimeHours: 39,
        actualWorkTimeMinutes: 0,
        shouldWorkTimeHours: 40,
        shouldWorkTimeMinutes: 0,
        actualHolidayDays: 0,
        shouldHolidayDaysInYear: 30
      }
    ]
  }
];

// Hilfsfunktionen
function formatMinutesDiff(minutes) {
  const sign = minutes >= 0 ? '+' : '-';
  const abs = Math.abs(minutes);
  const h = Math.floor(abs / 60);
  const m = abs % 60;
  return `${sign}${h}h ${m}min`;
}

const getAllYears = (data) => {
  const years = new Set();
  data.forEach((user) => {
    user.summaryCalendarEmployees.forEach((entry) => years.add(entry.year));
  });
  return Array.from(years).sort();
};

const getCurrentISOWeek = () => {
  const now = new Date();
  const jan4 = new Date(now.getFullYear(), 0, 4);
  const daysSinceJan4 = Math.floor((now - jan4) / 86400000);
  const weekNumber = Math.ceil((daysSinceJan4 + jan4.getDay() + 1) / 7);
  return Math.max(1, weekNumber);
};

const getDefaultCalendarWeek = () => {
  const currentWeek = getCurrentISOWeek();
  return currentWeek > 1 ? currentWeek - 1 : 1;
};

const SummaryEmployeeList = ({test=false}) => {
  const { keycloak,initialized } = useKeycloak();
  const [summaryData,setSummaryData]=useState([]);
  const [selectedYears, setSelectedYears] = useState();
  const [selectedCW, setSelectedCW] = useState(null);
  // Epic 33: the week clicked in the summary → opens the edit-week modal.
  const [weekModal, setWeekModal] = useState(null);
  const { t } = useTranslation();
  const { loading = false, error = null, summaryEmployee = [] } =
  useSelector((state) => state.summaryEmployee || {});

  const dispatch = useDispatch();

  const handleYearChange = (years) => {
    setSelectedYears(years);
    // Reset CW to default for new year selection
    setSelectedCW(null);
  };
  useEffect(() => {
    if (!initialized) return;
    if (test) {
      setSummaryData(testData);
    } else {
      
      dispatch(getSummaryEmployee());
      
      
    }
    setSelectedYears([new Date().getFullYear()]);
  }, [test, dispatch, initialized]);

  useEffect(() => {
    setSummaryData(summaryEmployee);
  }, [summaryEmployee]);

  // Default CW = current week - 1 (the last completed week)
  const defaultCW = useMemo(() => {
    return getDefaultCalendarWeek();
  }, []);

  // Effective CW: user-selected or default
  const effectiveCW = selectedCW !== null ? selectedCW : defaultCW;

  // Loading state for authentication
  if (!initialized) {
    return (
      <div className={styles.loadingContainer}>
        <Card className={styles.loadingCard}>
          <div className={styles.loadingContent}>
            <Spin
              indicator={<LoadingOutlined style={{ fontSize: 48, color: '#1890ff' }} spin />}
            />
            <Title level={4} style={{ marginTop: 24, color: '#595959' }}>
              {t("loadingAuth")}
            </Title>
            <Text type="secondary">{t("pleaseWait") || "Please wait..."}</Text>
          </div>
        </Card>
      </div>
    );
  }

  // Loading state for data
  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <Card className={styles.loadingCard}>
          <div className={styles.loadingContent}>
            <Spin
              indicator={<SyncOutlined style={{ fontSize: 48, color: '#52c41a' }} spin />}
            />
            <Title level={4} style={{ marginTop: 24, color: '#595959' }}>
              {t("loadingSummaryPlan")}
            </Title>
            <Text type="secondary">
              <BarChartOutlined style={{ marginRight: 8 }} />
              {t("fetchingEmployeeData") || "Fetching employee data..."}
            </Text>
          </div>
        </Card>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className={styles.loadingContainer}>
        <Result
          status="error"
          title={t("errorSummaryPlan")}
          subTitle={error}
          extra={
            <Text type="secondary">
              {t("tryAgainLater") || "Please try again later or contact support."}
            </Text>
          }
        />
      </div>
    );
  }


  return (
    <div className={styles.summaryContainer}>
      {/* Header Section */}
      <div className={styles.headerSection}>
        <div className={styles.titleContainer}>
          <BarChartOutlined className={styles.titleIcon} />
          <h1 className={styles.pageTitle}>{t("employeeSummary")}</h1>
        </div>
        <div className={styles.statsOverview}>
          <Statistic
            title={t("totalEmployees")}
            value={summaryData.length}
            prefix={<UserOutlined />}
            className={styles.statCard}
          />
          <Statistic
            title={t("selectedYears")}
            value={selectedYears?.length || 0}
            prefix={<CalendarOutlined />}
            className={styles.statCard}
          />
        </div>
      </div>

      {/* Filters Section */}
      <Card className={styles.filtersCard} title={
        <div className={styles.cardTitle}>
          <FilterOutlined className={styles.cardIcon} />
          <span>{t("filters")}</span>
        </div>
      }>
        <div className={styles.filtersContent}>
          <div className={styles.filterGroup}>
            <label className={styles.filterLabel}>
              <CalendarOutlined className={styles.labelIcon} />
              {t("selectYears")}
            </label>
            <Select
              mode="multiple"
              allowClear
              className={styles.yearSelect}
              placeholder={t("selectYearPlaceholder")}
              onChange={handleYearChange}
              value={selectedYears}
              size="large"
            >
              {getAllYears(summaryData).map((year) => (
                <Option key={year} value={year}>
                  {year}
                </Option>
              ))}
            </Select>
          </div>
          <div className={styles.filterGroup}>
            <label className={styles.filterLabel}>
              <ClockCircleOutlined className={styles.labelIcon} />
              {t("upToCalendarWeek")}
            </label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <InputNumber
                min={1}
                max={53}
                value={effectiveCW}
                onChange={(value) => setSelectedCW(value)}
                size="large"
                style={{ width: 100 }}
              />
              {selectedCW !== null && (
                <a
                  onClick={() => setSelectedCW(null)}
                  style={{ fontSize: 13, whiteSpace: 'nowrap' }}
                >
                  Reset
                </a>
              )}
            </div>
          </div>
        </div>
      </Card>

      {/* Employee Cards */}
      <div className={styles.employeeGrid}>
        {summaryData.map((employee) => {
          // Debug logging for j_abras
          if (employee.email === 'j_abras@hotmail.com' || employee.userName === 'j_abras@hotmail.com') {
            console.log('🔍 [j_abras] Raw calendar weeks:', employee.summaryCalendarEmployees?.map(c => `${c.year}-W${c.weekCalendarNumber}`));
            console.log('🔍 [j_abras] Selected years filter:', selectedYears);
          }

          //considering the holidays of the employee in the system (remainingHolidays)
          var currentRemaingHolidays=employee.remainingHolidays;
          var currentCalculatedYear=0;
          var availableHolidays={};

          // Create sorted copy
          const sortedCalendars = employee.summaryCalendarEmployees
            // Create a copy to avoid mutating original array
            .slice()
            // Sort by year first, then by week number (oldest first)
            .sort((a, b) => {
              if (a.year !== b.year) return a.year - b.year;  // Year ascending
              return a.weekCalendarNumber - b.weekCalendarNumber;  // Week ascending
            });

          // Debug after sorting
          if (employee.email === 'j_abras@hotmail.com' || employee.userName === 'j_abras@hotmail.com') {
            console.log('🔍 [j_abras] After sorting:', sortedCalendars.map(c => `${c.year}-W${c.weekCalendarNumber}`));
          }

          // Apply year filter
          const afterYearFilter = sortedCalendars.filter((e) => selectedYears.includes(e.year));

          // Apply CW filter
          const afterFilter = effectiveCW
            ? afterYearFilter.filter((e) => e.weekCalendarNumber <= effectiveCW)
            : afterYearFilter;

          // Debug after filtering
          if (employee.email === 'j_abras@hotmail.com' || employee.userName === 'j_abras@hotmail.com') {
            console.log('🔍 [j_abras] After year filter:', afterYearFilter.map(c => `${c.year}-W${c.weekCalendarNumber}`));
          }

          const filteredEntries = afterFilter
            .map((entry) => {
              const actual = entry.actualWorkTimeHours * 60 + entry.actualWorkTimeMinutes;
              const should = entry.shouldWorkTimeHours * 60 + entry.shouldWorkTimeMinutes;
              const diff = actual - should;
              if(currentCalculatedYear!=entry.year){
                currentRemaingHolidays=entry.shouldHolidayDaysInYear+currentRemaingHolidays;
                currentCalculatedYear=entry.year;
                availableHolidays[entry.year]=entry.shouldHolidayDaysInYear;
              }
              currentRemaingHolidays=currentRemaingHolidays-entry.actualHolidayDays;
              return {
                ...entry,
                diffMinutes: diff,
                diffFormatted: formatMinutesDiff(diff),
                remainingHolidays: currentRemaingHolidays,
              };
            });

          // Debug final result
          if (employee.email === 'j_abras@hotmail.com' || employee.userName === 'j_abras@hotmail.com') {
            console.log('🔍 [j_abras] Final entries to display:', filteredEntries.map(c => `${c.year}-W${c.weekCalendarNumber}`));
            console.log('🔍 [j_abras] Total entries:', filteredEntries.length);
          }

            // summing up the remaining holidays of the employee
          const grossMinutes = filteredEntries.reduce((sum, e) => sum + e.diffMinutes, 0)+employee.remainingOvertime*60;
          const grossFormatted = formatMinutesDiff(grossMinutes);

          // Taken Vacation: holiday days actually taken up to the current week.
          // filteredEntries is already bounded by the selected year(s) and the
          // "up to calendar week" filter (effectiveCW), so this equals the sum of
          // the per-week "Used Vacation Days" (actualHolidayDays) shown in the table.
          const takenVacationDays = filteredEntries.reduce((sum, e) => sum + (e.actualHolidayDays || 0), 0);

          // Paid overtime deduction
          const paidOvertimeMinutes = (employee.totalPaidOvertimeHours || 0) * 60 + (employee.totalPaidOvertimeMinutes || 0);
          const netMinutes = grossMinutes - paidOvertimeMinutes;
          const netFormatted = formatMinutesDiff(netMinutes);
          const hasPaidOvertime = paidOvertimeMinutes > 0;

          // Calculate performance indicators (based on net balance)
          const performanceScore = netMinutes >= 0 ? 'positive' : 'negative';
          
         

          const calendarColumns = [
            {
              title: t("year"),
              dataIndex: 'year',
              key: 'year',
              width: 80,
              align: 'center',
              render: (year) => (
                <Tag color="blue" className={styles.yearTag}>
                  {year}
                </Tag>
              )
            },
            {
              title: t("calendarWeek"),
              dataIndex: 'weekCalendarNumber',
              key: 'weekCalendarNumber',
              width: 100,
              align: 'center',
              // Display-only sort — the running holiday totals baked into each row
              // (see the .map above) stay correct regardless of render order.
              // Year is the primary key so weeks don't interleave across years
              // when several are selected. No defaultSortOrder → the table shows
              // the pre-sorted ascending order until the user clicks. The data is
              // already ascending, so the cycle leads with 'descend' — the first
              // click does something visible (newest week first), matching the
              // mobile sheet's first tap.
              sorter: (a, b) =>
                (a.year - b.year) || (a.weekCalendarNumber - b.weekCalendarNumber),
              sortDirections: ['descend', 'ascend'],
              render: (week) => (
                <Text strong className={styles.weekNumber}>
                  {t("week")} {week}
                </Text>
              )
            },
            {
              title: t("actualTime"),
              key: 'actualTime',
              width: 120,
              align: 'center',
              render: (record) => (
                <div className={styles.timeDisplay}>
                  <ClockCircleOutlined className={styles.timeIcon} />
                  <span>{record.actualWorkTimeHours}h {record.actualWorkTimeMinutes}min</span>
                </div>
              )
            },
            {
              title: t("plannedTime"),
              key: 'shouldTime',
              width: 120,
              align: 'center',
              render: (record) => (
                <div className={styles.timeDisplay}>
                  <CalendarOutlined className={styles.timeIcon} />
                  <span>{record.shouldWorkTimeHours}h {record.shouldWorkTimeMinutes}min</span>
                </div>
              )
            },
            {
              title: t("difference"),
              key: 'diff',
              width: 120,
              align: 'center',
              render: (record) => {
                const isPositive = record.diffMinutes >= 0;
                return (
                  <Tag 
                    color={isPositive ? 'success' : 'error'}
                    icon={isPositive ? <CheckCircleOutlined /> : <ExclamationCircleOutlined />}
                    className={styles.diffTag}
                  >
                    {record.diffFormatted}
                  </Tag>
                );
              }
            },
            {
              title: t("usedVacationDays"),
              key: 'actualHolidayDays',
              width: 140,
              align: 'center',
              render: (record) => (
                <div className={styles.holidayDisplay}>
                  <TrophyOutlined className={styles.holidayIcon} />
                  <span>{record.actualHolidayDays} {t("days_name")}</span>
                </div>
              )
            },
            {
              title: t("remainingVacationDays"),
              key: 'remainingHolidays',
              width: 160,
              align: 'center',
              render: (record) => (
                <div className={styles.holidayDisplay}>
                  <CalendarOutlined className={styles.holidayIcon} />
                  <span>{record.remainingHolidays} {t("days_name")}</span>
                </div>
              )
            }
          ];

          return (
            <Card
              key={employee.userId}
              className={styles.employeeCard}
              hoverable
            >
              {/* Employee Header */}
              <div className={styles.employeeHeader}>
                <div className={styles.employeeInfo}>
                  <Avatar 
                    size={64} 
                    icon={<UserOutlined />} 
                    className={styles.employeeAvatar}
                  />
                  <div className={styles.employeeDetails}>
                    <Title level={4} className={styles.employeeName}>
                      {employee.userName}
                    </Title>
                    <Text type="secondary" className={styles.employeeEmail}>
                      {employee.email}
                    </Text>
                  </div>
                </div>
                <div className={styles.performanceIndicator}>
                  <Tag 
                    color={performanceScore === 'positive' ? 'success' : 'warning'}
                    icon={performanceScore === 'positive' ? <CheckCircleOutlined /> : <ExclamationCircleOutlined />}
                    className={styles.performanceTag}
                  >
                    {performanceScore === 'positive' ? t('onTrack') : t('needsAttention')}
                  </Tag>
                </div>
              </div>

              {/* Summary Statistics */}
              <div className={styles.summaryStats}>
                <Row gutter={16}>
                  <Col xs={24} sm={12} md={hasPaidOvertime ? 8 : 4}>
                    {hasPaidOvertime ? (
                      <div className={styles.overtimeBreakdown}>
                        <span className={styles.breakdownLabel}>{t("overtimeBalance")}</span>
                        <div className={styles.breakdownRow}>
                          <Text type="secondary" style={{ fontSize: 13 }}>{t("grossOvertime")}:</Text>
                          <Text style={{ fontSize: 14, color: grossMinutes >= 0 ? '#3f8600' : '#cf1322' }}>
                            {grossFormatted}
                          </Text>
                        </div>
                        <div className={styles.breakdownRow}>
                          <Text type="secondary" style={{ fontSize: 13 }}>{t("paidOvertime.paidOvertimeSummary")}:</Text>
                          <Text style={{ fontSize: 14, color: '#13c2c2' }}>
                            -{employee.totalPaidOvertimeHours}h {employee.totalPaidOvertimeMinutes}m
                          </Text>
                        </div>
                        <div className={`${styles.breakdownRow} ${styles.breakdownDivider}`}>
                          <Text strong style={{ fontSize: 13 }}>{t("netBalance")}:</Text>
                          <Text strong style={{ fontSize: 18, color: netMinutes >= 0 ? '#3f8600' : '#cf1322' }}>
                            {netMinutes >= 0 ? <CheckCircleOutlined /> : <ExclamationCircleOutlined />} {netFormatted}
                          </Text>
                        </div>
                      </div>
                    ) : (
                      <Statistic
                        title={t("totalTimeDifference")}
                        value={netFormatted}
                        valueStyle={{
                          color: netMinutes >= 0 ? '#3f8600' : '#cf1322',
                          fontSize: '18px',
                          fontWeight: 'bold'
                        }}
                        prefix={netMinutes >= 0 ? <CheckCircleOutlined /> : <ExclamationCircleOutlined />}
                      />
                    )}
                  </Col>
                  <Col xs={24} sm={12} md={hasPaidOvertime ? 4 : 5}>
                    <Statistic
                      title={t("vacationCarryover")}
                      value={employee.remainingHolidays}
                      suffix={t("days_name")}
                      valueStyle={{ color: '#fa8c16', fontSize: '18px', fontWeight: 'bold' }}
                      prefix={<CalendarOutlined />}
                    />
                  </Col>
                  <Col xs={24} sm={12} md={hasPaidOvertime ? 4 : 5}>
                    <Statistic
                      title={t("currentYearVacation")}
                      value={currentRemaingHolidays}
                      suffix={t("days_name")}
                      valueStyle={{ color: '#1890ff', fontSize: '18px', fontWeight: 'bold' }}
                      prefix={<TrophyOutlined />}
                    />
                  </Col>
                  <Col xs={24} sm={12} md={hasPaidOvertime ? 4 : 5}>
                    <Statistic
                      title={
                        <span>
                          {t("takenVacation")}{' '}
                          <Tooltip title={t("takenVacationTooltip")}>
                            <QuestionCircleOutlined style={{ color: '#8c8c8c', cursor: 'help' }} />
                          </Tooltip>
                        </span>
                      }
                      value={takenVacationDays}
                      suffix={t("days_name")}
                      valueStyle={{ color: '#13c2c2', fontSize: '18px', fontWeight: 'bold' }}
                      prefix={<ScheduleOutlined />}
                    />
                  </Col>
                  <Col xs={24} sm={12} md={hasPaidOvertime ? 4 : 5}>
                    <Statistic
                      title={t("remainingOvertime")}
                      value={employee.remainingOvertime}
                      suffix={t("hours")}
                      valueStyle={{ color: '#722ed1', fontSize: '18px', fontWeight: 'bold' }}
                      prefix={<ClockCircleOutlined />}
                    />
                  </Col>
                </Row>
              </div>

            

              {/* Data Table */}
              <div className={styles.tableSection}>
                <Title level={5} className={styles.sectionTitle}>
                  <BarChartOutlined className={styles.sectionIcon} />
                  {t("detailedBreakdown")}
                </Title>
                <Table
                  dataSource={filteredEntries}
                  columns={calendarColumns}
                  pagination={{
                    pageSize: 10,
                    size: 'small',
                    showSizeChanger: false,
                    showQuickJumper: false
                  }}
                  size="small"
                  rowKey={(record, index) => `${employee.userId}-${index}`}
                  scroll={{ x: true }}
                  onRow={(record) => ({
                    onClick: () => setWeekModal({
                      userId: employee.userId,
                      year: record.year,
                      week: record.weekCalendarNumber,
                      userName: employee.userName,
                    }),
                    style: { cursor: 'pointer' },
                  })}
                  className={styles.employeeTable}
                  locale={{
                    emptyText: (
                      <div className={styles.emptyState}>
                        <MinusCircleOutlined className={styles.emptyIcon} />
                        <Text type="secondary">{t("noDataForYears")}</Text>
                      </div>
                    )
                  }}
                  footer={() =>
                    filteredEntries.length > 0 ? (
                      <div className={styles.tableFooter}>
                        <div className={styles.footerStats}>
                          <Tag color="blue" className={styles.footerTag}>
                            {t("totalEntries")}: {filteredEntries.length}
                          </Tag>
                          <Tag
                            color={netMinutes >= 0 ? 'success' : 'error'}
                            className={styles.footerTag}
                          >
                            {hasPaidOvertime ? t("netBalance") : t("totalDifference")}: {netFormatted}
                          </Tag>
                          {hasPaidOvertime && (
                            <Tag color="cyan" className={styles.footerTag}>
                              {t("paidOvertime.paidOvertimeSummary")}: -{employee.totalPaidOvertimeHours}h {employee.totalPaidOvertimeMinutes}m
                            </Tag>
                          )}
                          <Tag color="purple" className={styles.footerTag}>
                            {t("remainingVacationDays")}: {currentRemaingHolidays} {t("days_name")}
                          </Tag>
                        </div>
                      </div>
                    ) : null
                  }
                />
              </div>
            </Card>
          );
        })}
      </div>
      
      {/* Empty State */}
      {summaryData.length === 0 && (
        <div className={styles.emptyState}>
          <UserOutlined className={styles.emptyIcon} />
          <Title level={3} className={styles.emptyTitle}>{t("noEmployeeData")}</Title>
          <Text type="secondary" className={styles.emptyDescription}>
            {t("noEmployeeDataDescription")}
          </Text>
        </div>
      )}

      {/* Epic 33: edit a specific week's shifts/times from the summary */}
      <WeekDetailModal
        open={!!weekModal}
        onClose={() => setWeekModal(null)}
        userId={weekModal?.userId}
        year={weekModal?.year}
        week={weekModal?.week}
        userName={weekModal?.userName}
      />
    </div>
  );
};

export default SummaryEmployeeList;
